import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLocale } from "../i18n";
import { useConcierge } from "../lib/concierge";
import { MessageBody } from "./concierge/MessageBody";
import { TripPanel, CostCard, ChatError } from "./concierge/TripPanel";

export default function ConciergeWidget() {
  const { locale, t } = useLocale();
  const { messages, streaming, send: sendMsg } = useConcierge();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = (msgText) => {
    const text = (msgText ?? input).trim();
    if (!text) return;
    sendMsg(text, locale);
    setInput("");
  };

  const starters = locale === "ro" ? t("concierge.starters_ro") : t("concierge.starters_en");

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="concierge-widget-open"
          className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 pl-4 pr-5 py-3 bg-[var(--wr-night)] text-[var(--wr-bg)] rounded-full shadow-[0_8px_30px_rgba(10,17,12,0.35)] hover:bg-[var(--wr-red)] transition-colors duration-300"
        >
          <MessageCircle size={18} />
          <span className="font-mono-wr text-[11px] uppercase tracking-widest">
            {locale === "ro" ? "Întreabă Ghidul" : "Ask Concierge"}
          </span>
        </button>
      )}
      {open && (
        <div
          data-testid="concierge-widget"
          className="fixed bottom-6 right-6 z-40 w-[min(calc(100vw-3rem),420px)] h-[min(72vh,620px)] flex flex-col rounded-md overflow-hidden bg-[var(--wr-night)] text-[var(--wr-bg)] border border-white/10 shadow-[0_20px_60px_rgba(10,17,12,0.5)] backdrop-blur-xl"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <p className="font-editorial text-xl leading-none">Concierge</p>
              <p className="font-mono-wr text-[9px] uppercase tracking-widest text-white/50 mt-1">WOWROMANIA</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              data-testid="concierge-widget-close"
              aria-label="close"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4" data-testid="concierge-messages">
            {messages.length === 0 && (
              <div className="space-y-4">
                <p className="font-editorial text-2xl leading-tight">
                  {locale === "ro" ? "Ce vrei să știi despre România?" : "What do you want to know about Romania?"}
                </p>
                <div className="grid gap-2 pt-2">
                  {starters.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`concierge-starter-${i}`}
                      className="text-left text-[13px] px-4 py-3 border border-white/10 hover:border-[var(--wr-red)] rounded-sm transition-colors duration-300 text-white/85"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-md px-4 py-3 text-[14px] leading-relaxed wr-msg ${
                  m.role === "user"
                    ? "bg-[var(--wr-pine)] text-white"
                    : "bg-[#121C16] text-white/90 border border-white/5"
                }`}>
                  {m.content ? <MessageBody role={m.role} content={m.content} /> : (m.pending ? <Loader2 size={14} className="animate-spin" /> : "…")}
                  {m.costs && <CostCard costs={m.costs} locale={locale} />}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 pt-2 space-y-2">
            <ChatError locale={locale} />
            <TripPanel locale={locale} />
          </div>
          <div className="border-t border-white/10 px-4 py-3 mt-2 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={t("concierge.placeholder")}
              className="flex-1 bg-transparent border-0 focus:outline-none text-[14px] text-white placeholder-white/40 font-body"
              data-testid="concierge-widget-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim()}
              className="p-2 rounded-full bg-[var(--wr-red)] hover:bg-[var(--wr-red-hover)] disabled:opacity-40 transition-colors"
              data-testid="concierge-widget-send"
              aria-label="send"
            >
              {streaming && !input.trim() ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
